import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Download,
  Upload,
  Loader2,
  Shield,
  Check,
  X,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Cpu,
  LayoutGrid,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { useQueryClient } from '@tanstack/react-query';
import { useRcmTablePage } from '../hooks/rcm/useRcmTablePage.ts';
import { fetchRcmTablePage, fetchRcmAllTablePage } from '../request/rcm/functional.js';
import { rcmQueryKeys } from '../queries/rcm/keys.ts';
import { normalizePageSize, readStoredListPageSize, writeStoredListPageSize } from '../shared/table/pagination.ts';
import { RcmListFilters } from './rcm-table/RcmListFilters.jsx';
import { RcmMatrixTable } from './rcm-table/RcmMatrixTable.jsx';
import { useRcmListFilters } from './rcm-table/useRcmListFilters.js';
import RcmItgcView from './rcm-table/RcmItgcView.jsx';
import RcmAllView from './rcm-table/RcmAllView.jsx';
import { GenericImportRcmModal } from './rcm-table/generic-import';
import { RcmColumnVisibilityMenu } from './rcm-table/RcmColumnVisibilityMenu.jsx';
import {
  createDefaultRcmColumnVisibility,
  getDefaultRcmColumnOrder,
  getHideableRcmColumns,
  getOrderedVisibleRcmColumnIds,
  getVisibleRcmColumnCount,
  isRcmColumnVisible,
  mergeRcmHideableOrder,
  RCM_TABLE_COLUMNS,
} from './rcm-table/rcmTableColumnConfig.js';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from './shared/ModuleAuditDownloadButton.tsx';
import {
  mapApiItemToRcmRow,
  mapAllApiItemToRow,
  formatRcmClassificationLabel,
  formatRcmKeyReportsPreview,
} from './rcm-table/rcmAllRowMappers.js';
import { RISK_CATEGORY_BADGE_CLASSES } from '../constants/riskCategories';
import { RCM_ALL_TABLE_COLUMNS } from './rcm-table/rcmAllTableColumnConfig.js';
import { RCM_ITGC_TABLE_COLUMNS } from './rcm-table/rcmItgcTableColumnConfig.js';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { Tooltip } from '../admin/components/shared/Tooltip.tsx';
import {
  clearStoredRcmColumnPreferences,
  readStoredRcmColumnOrder,
  readStoredRcmColumnVisibility,
  writeStoredRcmColumnOrder,
  writeStoredRcmColumnVisibility,
} from './rcm-table/rcmColumnPreferences.ts';

const RCM_PROCESS_COLUMN_IDS = RCM_TABLE_COLUMNS.map((column) => column.id);

/** Build Excel row objects using only currently visible columns (gear order). */
function buildExportSheetRows(dataRows, columns, orderedVisibleIds) {
  const colById = Object.fromEntries(columns.map((c) => [c.id, c]));
  const ids = Array.isArray(orderedVisibleIds) && orderedVisibleIds.length > 0
    ? orderedVisibleIds
    : columns.map((c) => c.id);
  return dataRows.map((row) => {
    const out = {};
    for (const id of ids) {
      const col = colById[id];
      if (!col) continue;
      out[col.label] = row[id] ?? '';
    }
    return out;
  });
}

// Scope tabs — Process is the existing financial-process matrix; ITGC is
// the system × standard-control pivot driven by backend PR #150/#151.
// Tab is persisted in ?scope= so deep-links work.
const SCOPE_PROCESS = 'process';
const SCOPE_ITGC = 'itgc';
const SCOPE_ALL = 'all';
// Imported RCM rows are no longer a separate scope: an RCM import writes
// business rows into the Process matrix (process_risk_controls) and ITGC rows
// into the ITGC matrix (system_itgc_controls), so they surface in those two
// tabs directly.
const VALID_SCOPES = new Set([SCOPE_PROCESS, SCOPE_ITGC, SCOPE_ALL]);

const RCM_CELL_CHAR_LIMITS = {
  default: 42,
  soxCycle: 26,
  subProcess: 30,
  riskId: 24,
  clientRiskId: 24,
  controlId: 24,
  riskDescription: 96,
  controlObjective: 88,
  controlDescription: 96,
  what: 72,
  who: 56,
  keyReports: 72,
  when: 48,
  controlType: 20,
};

function truncateCellText(value, field = 'default') {
  const text = value == null || value === '' ? '-' : String(value);
  const limit = RCM_CELL_CHAR_LIMITS[field] ?? RCM_CELL_CHAR_LIMITS.default;
  if (text.length <= limit || text === '-') {
    return { displayValue: text, fullValue: text, isTruncated: false };
  }
  return {
    displayValue: `${text.slice(0, limit)}...`,
    fullValue: text,
    isTruncated: true,
  };
}

function TruncatedCellValue({ value, field = 'default', className = '', side = 'top' }) {
  const { displayValue, fullValue, isTruncated } = truncateCellText(value, field);
  const content = <span className={className}>{displayValue}</span>;
  if (!isTruncated) return content;
  return (
    <Tooltip content={fullValue} side={side}>
      {content}
    </Tooltip>
  );
}

function RiskControlMatrix() {
  // Active scope tab — persisted in ?scope= so deep-links land on the
  // right tab (e.g. /risk-control-matrix?scope=itgc).
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeFromUrl = searchParams.get('scope');
  const scope = VALID_SCOPES.has(scopeFromUrl) ? scopeFromUrl : SCOPE_PROCESS;
  const setScope = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === SCOPE_PROCESS) params.delete('scope');
    else params.set('scope', next);
    setSearchParams(params, { replace: true });
  };

  // Import RCM (map columns) — opens from the header action button. On a
  // successful import we refetch so new rows are visible (ITGC under ITGC tab).
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [itgcReloadToken, setItgcReloadToken] = useState(0);
  const queryClient = useQueryClient();

  // ITGC stats are computed inside RcmItgcView (it owns the fetch) and
  // surfaced here via callback so the widget row above the tabs can
  // render them without duplicating the fetch.
  const [itgcStats, setItgcStats] = useState(null);
  // Filtered ITGC table rows for Excel export (mirrors Process/All filter export).
  const [itgcFilteredRows, setItgcFilteredRows] = useState([]);
  const [itgcExportColumnIds, setItgcExportColumnIds] = useState([]);
  const [allExportColumnIds, setAllExportColumnIds] = useState([]);
  const [allStats, setAllStats] = useState(null);
  const allListParamsRef = useRef({ page: 1, pageSize: readStoredListPageSize() });
  const handleAllListParamsChange = useCallback((params) => {
    allListParamsRef.current = params;
  }, []);

  const [rcmListPage, setRcmListPage] = useState(1);
  const [rcmPageSize, setRcmPageSize] = useState(readStoredListPageSize);
  const [rcmColumnVisibility, setRcmColumnVisibility] = useState(() =>
    readStoredRcmColumnVisibility('process', createDefaultRcmColumnVisibility, RCM_PROCESS_COLUMN_IDS)
  );
  const [rcmColumnOrder, setRcmColumnOrder] = useState(() =>
    readStoredRcmColumnOrder('process', getDefaultRcmColumnOrder, RCM_PROCESS_COLUMN_IDS)
  );
  const [rcmData, setRcmData] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, field }
  const [editValue, setEditValue] = useState('');

  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const {
    searchQuery,
    setSearchQuery,
    filterControlType,
    setFilterControlType,
    filterSignificance,
    setFilterSignificance,
    filterFrequency,
    setFilterFrequency,
    filterRiskCategory,
    setFilterRiskCategory,
    filterProcess,
    filterControlId,
    processFilterOptions,
    controlFilterOptions,
    processFilterNoOptionsMessage,
    controlFilterNoOptionsMessage,
    loadingMoreProcessFilterOptions,
    handleProcessFilterChange,
    handleProcessFilterInputChange,
    handleProcessFilterMenuClose,
    handleProcessFilterMenuScrollToBottom,
    handleControlFilterChange,
    handleControlFilterInputChange,
    handleControlFilterMenuClose,
    resetFilters: resetListFilters,
    apiFilterParams,
    hasActiveListCriteria,
  } = useRcmListFilters();

  const [sortState, setSortState] = useState({ key: null, dir: 'asc' });
  const sortKey = sortState.key;
  const sortDir = sortState.dir;

  useEffect(() => {
    setRcmListPage(1);
  }, [apiFilterParams, sortKey, sortDir, selectedAuditYearId]);

  const listParams = useMemo(
    () => ({
      page: rcmListPage,
      pageSize: rcmPageSize,
      ...apiFilterParams,
      sortKey,
      sortDir,
      auditYearId: selectedAuditYearId || undefined,
    }),
    [rcmListPage, rcmPageSize, apiFilterParams, sortKey, sortDir, selectedAuditYearId]
  );

  const handleSortClick = useCallback((key) => {
    setSortState((prev) =>
      prev.key === key ? { key: prev.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  }, []);

  const renderSortIcon = useCallback(
    (key) => {
      if (sortKey !== key) {
        return <ArrowUpDown className="h-3 w-3 text-gray-400 shrink-0" aria-hidden />;
      }
      return sortDir === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-teal-300 shrink-0" aria-hidden />
      ) : (
        <ArrowDown className="h-3 w-3 text-teal-300 shrink-0" aria-hidden />
      );
    },
    [sortKey, sortDir]
  );

  const visibleRcmColumnCount = useMemo(
    () => getVisibleRcmColumnCount(rcmColumnVisibility),
    [rcmColumnVisibility]
  );

  const orderedVisibleRcmColumnIds = useMemo(
    () => getOrderedVisibleRcmColumnIds(rcmColumnOrder, rcmColumnVisibility),
    [rcmColumnOrder, rcmColumnVisibility]
  );

  const handleToggleRcmColumn = useCallback((columnId) => {
    setRcmColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !isRcmColumnVisible(prev, columnId),
    }));
  }, []);

  const handleReorderRcmColumns = useCallback((orderedHideableIds) => {
    setRcmColumnOrder((prev) => mergeRcmHideableOrder(prev, orderedHideableIds));
  }, []);

  const handleResetRcmColumns = useCallback(() => {
    clearStoredRcmColumnPreferences('process');
    setRcmColumnVisibility(createDefaultRcmColumnVisibility());
    setRcmColumnOrder(getDefaultRcmColumnOrder());
  }, []);

  useEffect(() => {
    writeStoredRcmColumnVisibility('process', rcmColumnVisibility);
  }, [rcmColumnVisibility]);

  useEffect(() => {
    writeStoredRcmColumnOrder('process', rcmColumnOrder);
  }, [rcmColumnOrder]);

  const handleResetFilters = useCallback(() => {
    resetListFilters();
    setSortState({ key: null, dir: 'asc' });
    setRcmListPage(1);
  }, [resetListFilters]);

  const {
    data,
    isFetching,
    isPlaceholderData,
    isLoading,
  } = useRcmTablePage(listParams, { enabled: scope === SCOPE_PROCESS });

  const rcmTotalItems = data?.total ?? 0;
  const rcmTotalPages = data?.totalPages ?? 1;
  const hasData = rcmTotalItems > 0;

  const tableLoading = isFetching && (!data || isPlaceholderData);
  const showTableOverlay = tableLoading && rcmData.length > 0;

  useLayoutEffect(() => {
    if (!data?.items) return;
    if (isPlaceholderData && isFetching) return;
    const p = data.page ?? listParams.page;
    const baseSr = (p - 1) * listParams.pageSize;
    setRcmData(data.items.map((item, idx) => mapApiItemToRcmRow(item, baseSr + idx + 1)));
  }, [data, isPlaceholderData, isFetching, listParams.page, listParams.pageSize]);

  useEffect(() => {
    setRcmListPage((p) => Math.min(Math.max(1, p), Math.max(1, rcmTotalPages)));
  }, [rcmTotalPages]);

  const handleTablePaginationChange = useCallback(
    (updater) => {
      const maxP = Math.max(1, rcmTotalPages);
      const safePrev = Math.min(Math.max(1, rcmListPage), maxP);
      const cur = { pageIndex: safePrev - 1, pageSize: rcmPageSize };
      const next = typeof updater === 'function' ? updater(cur) : updater;
      if (next.pageSize !== rcmPageSize) {
        const nextPageSize = normalizePageSize(next.pageSize);
        setRcmPageSize(nextPageSize);
        writeStoredListPageSize(nextPageSize);
        setRcmListPage(1);
        return;
      }
      setRcmListPage(Math.min(Math.max(1, next.pageIndex + 1), maxP));
    },
    [rcmListPage, rcmPageSize, rcmTotalPages]
  );

  const paginationFrom = useMemo(() => {
    if (rcmTotalItems === 0) return 0;
    return (rcmListPage - 1) * rcmPageSize + 1;
  }, [rcmListPage, rcmPageSize, rcmTotalItems]);

  const paginationTo = useMemo(() => {
    if (rcmTotalItems === 0) return 0;
    return Math.min(rcmListPage * rcmPageSize, rcmTotalItems);
  }, [rcmListPage, rcmPageSize, rcmTotalItems]);

  // Handle cell edit
  const startEditing = (rowIndex, field, currentValue) => {
    setEditingCell({ rowIndex, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Save a single field override to the backend
  const saveOverride = async (mappingId, field, value) => {
    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.put(`/api/risk-control-mappings/${mappingId}/rcm-overrides`, {
        overrides: { [field]: value }
      }, {
        headers: { 'Authorization': userHash || '' }
      });
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving override:', error);
    }
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    setSaving(true);

    const { rowIndex, field } = editingCell;
    const updatedData = [...rcmData];
    updatedData[rowIndex] = { ...updatedData[rowIndex], [field]: editValue };
    setRcmData(updatedData);
    setEditingCell(null);
    setEditValue('');

    const row = updatedData[rowIndex];
    await saveOverride(row.mappingId, field, editValue);

    setSaving(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEditing();
  };

  // Export to Excel (all pages — same filters/sort as table list; visible columns only)
  const handleExport = useCallback(async () => {
    if (exporting || rcmTotalItems === 0) return;
    setExporting(true);
    try {
      const first = await fetchRcmTablePage({ ...listParams, page: 1 });
      const allItems = [...first.items];
      for (let p = 2; p <= first.totalPages; p++) {
        const pageRes = await fetchRcmTablePage({ ...listParams, page: p });
        allItems.push(...pageRes.items);
      }
      const rows = allItems.map((item, i) => mapApiItemToRcmRow(item, i + 1));
      const exportData = buildExportSheetRows(rows, RCM_TABLE_COLUMNS, orderedVisibleRcmColumnIds);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Risk Control Matrix');
      XLSX.writeFile(wb, 'Risk_Control_Matrix.xlsx');
    } catch (e) {
      console.error('Export RCM failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting, rcmTotalItems, listParams, orderedVisibleRcmColumnIds]);

  // Export All tab to Excel (all pages; visible columns only)
  const handleAllExport = useCallback(async () => {
    const total = allStats?.rows ?? 0;
    if (exporting || total === 0) return;
    setExporting(true);
    try {
      const { pageSize: storedPageSize, ...filterParams } = allListParamsRef.current ?? {};
      const pageSize = normalizePageSize(storedPageSize ?? readStoredListPageSize());
      const baseParams = { ...filterParams, pageSize };
      const first = await fetchRcmAllTablePage({ ...baseParams, page: 1 });
      const allItems = [...first.items];
      for (let p = 2; p <= first.totalPages; p++) {
        const pageRes = await fetchRcmAllTablePage({ ...baseParams, page: p });
        allItems.push(...pageRes.items);
      }
      const rows = allItems.map((item, i) => mapAllApiItemToRow(item, i + 1)).filter(Boolean);
      const exportData = buildExportSheetRows(rows, RCM_ALL_TABLE_COLUMNS, allExportColumnIds);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Risk Control Matrix');
      XLSX.writeFile(wb, 'Risk_Control_Matrix.xlsx');
    } catch (e) {
      console.error('Export RCM All failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting, allStats?.rows, allExportColumnIds]);

  // Export ITGC tab to Excel (filtered rows + visible columns only)
  const handleItgcExport = useCallback(async () => {
    if (exporting || itgcFilteredRows.length === 0) return;
    setExporting(true);
    try {
      const items = itgcFilteredRows;
      const rows = items.map((item, idx) => {
        const isKey = (item.significance || '').toLowerCase() === 'key';
        const controlTypeRaw = item.controlType;
        const controlType =
          controlTypeRaw === 'manual'
            ? 'Manual'
            : controlTypeRaw === 'automated'
              ? 'Automated'
              : controlTypeRaw || '';
        return {
          srNo: idx + 1,
          classification: formatRcmClassificationLabel(item.classification || 'itgc'),
          domain: item.domainCode || '',
          system: item.systemName || '',
          controlId: item.controlId || '',
          riskId: item.riskId || '',
          riskDescription: item.riskDescription || '',
          controlObjective: item.controlObjective || '',
          controlDescription: item.controlDescription || '',
          frequency: item.frequency || '',
          controlType,
          keyNonKey: isKey ? 'Key' : 'Non-Key',
        };
      });
      const exportData = buildExportSheetRows(rows, RCM_ITGC_TABLE_COLUMNS, itgcExportColumnIds);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Risk Control Matrix');
      XLSX.writeFile(wb, 'Risk_Control_Matrix.xlsx');
    } catch (e) {
      console.error('Export RCM ITGC failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting, itgcFilteredRows, itgcExportColumnIds]);


  // Editable cell renderer
  const EditableCell = ({ rowIndex, field, value, className = '', isDropdown = false, options = [] }) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      if (isDropdown) {
        return (
          <div className="flex items-center gap-1">
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-1 py-0.5 text-xs border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
              autoFocus
            >
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button onClick={saveEdit} className="text-teal-600 hover:text-teal-800"><Check className="h-3 w-3" /></button>
            <button onClick={cancelEditing} className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-1 py-0.5 text-xs border border-teal-400 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
            autoFocus
          />
          <button onClick={saveEdit} className="text-teal-600 hover:text-teal-800 flex-shrink-0"><Check className="h-3 w-3" /></button>
          <button onClick={cancelEditing} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="h-3 w-3" /></button>
        </div>
      );
    }

    return (
      <div
        className={`group relative flex items-center justify-center cursor-pointer hover:bg-teal-50 rounded px-1 py-0.5 transition-colors ${className}`}
        onClick={() => startEditing(rowIndex, field, value)}
        title="Click to edit"
      >
        <TruncatedCellValue
          value={value}
          field={field}
          className="text-center text-[11px] leading-tight font-normal text-gray-800"
          side="top"
        />
        <Pencil className="absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-gray-300 opacity-0 transition-opacity group-hover:text-teal-500 group-hover:opacity-100" />
      </div>
    );
  };

  // Assertion cell - editable dropdown
  const AssertionCell = ({ rowIndex, field, value }) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      return (
        <select
          value={editValue}
          onChange={(e) => {
            const newVal = e.target.value;
            setEditValue(newVal);
            // Auto-save on selection
            const updatedData = [...rcmData];
            updatedData[rowIndex] = { ...updatedData[rowIndex], [field]: newVal };
            setRcmData(updatedData);
            setEditingCell(null);
            // Persist to backend
            saveOverride(updatedData[rowIndex].mappingId, field, newVal);
          }}
          className="w-full px-0.5 py-0.5 text-xs border border-teal-400 rounded focus:outline-none"
          autoFocus
          onBlur={cancelEditing}
        >
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    }

    return (
      <span
        className={`text-xs font-medium cursor-pointer hover:opacity-70 ${value === 'Yes' ? 'text-green-700' : 'text-gray-400'}`}
        onClick={() => startEditing(rowIndex, field, value)}
        title="Click to toggle"
      >
        {value}
      </span>
    );
  };

  if (scope === SCOPE_PROCESS && isLoading && rcmData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
          <p className="text-sm text-gray-500">Loading Risk Control Matrix...</p>
        </div>
      </div>
    );
  }

  // Active scope drives which tab content shows. ?scope=itgc deep-links
  // into the ITGC tab; anything else (including missing) stays on Process.
  const activeScope = scope;

  // Stats for the widget row above the tabs. Process side derives from
  // rcmData (already loaded for the matrix) so the numbers stay in sync
  // with whatever the current filters return. `rows` is the total set
  // matching the filters (rcmTotalItems), not just the current page.
  const currentYear = selectedAuditYear?.name ?? new Date().getFullYear();
  const processStats = {
    rows: rcmTotalItems,
    cycles: new Set(rcmData.map((r) => r.soxCycle).filter(Boolean)).size,
    keyControls: rcmData.filter((r) => r.keyNonKey === 'Key').length,
  };

  const processEmptyState = (
    <div className="bg-white rounded-lg border border-gray-200 p-10 text-center mt-4">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <Shield className="h-8 w-8 text-gray-400" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">No process RCM mappings yet</h2>
      <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
        To populate the Process RCM, identify risks on a flowchart, configure controls for each risk
        on the Risks page, and they&rsquo;ll show up here.
      </p>
      <div className="bg-white rounded-lg border border-gray-200 p-5 max-w-md mx-auto text-left space-y-3">
        <Step n={1} title="Identify risks" body='Open a flowchart and click "Identify Risks" to analyze process steps for SOX risks.' />
        <Step n={2} title="Configure controls" body='On the Risks page click "Configure Control" to map a control to each risk.' />
        <Step n={3} title="View RCM" body="Once mappings are configured, the matrix populates here automatically." />
      </div>
    </div>
  );

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 w-full">
        <ReadOnlyBanner />

        {/* Header — typography matches SOX Risks list */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2 shrink-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600 shrink-0" aria-hidden />
              Risk Control Matrix
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {saveMessage && (
              <span className="text-xs text-teal-600 flex items-center gap-1">
                <Check className="h-3 w-3" aria-hidden /> {saveMessage}
              </span>
            )}
            <ModuleAuditDownloadButton
              resourceType="rcm"
              moduleKeys={[MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS]}
              filenamePrefix="rcm_audit"
            />
            <button
              type="button"
              onClick={() => setImportModalOpen(true)}
              disabled={isLocked}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-teal-700 bg-white border border-teal-600 rounded-lg hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="h-4 w-4" aria-hidden /> Import RCM
            </button>
            {activeScope === SCOPE_PROCESS && (
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || rcmTotalItems === 0}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                Export to Excel
              </button>
            )}
            {activeScope === SCOPE_ALL && (
              <button
                type="button"
                onClick={handleAllExport}
                disabled={exporting || (allStats?.rows ?? 0) === 0}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                Export to Excel
              </button>
            )}
            {activeScope === SCOPE_ITGC && (
              <button
                type="button"
                onClick={handleItgcExport}
                disabled={exporting || itgcFilteredRows.length === 0}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                Export to Excel
              </button>
            )}
          </div>
        </div>

        {/* Stat widgets — live above the scope tabs. Numbers come from
            real data: process side from rcmData (already loaded for the
            matrix); ITGC side surfaced from RcmItgcView via callback so
            the fetch happens once. The card SET swaps with the active
            scope so the row always reflects "stats for the matrix you
            are looking at". */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
          {activeScope === SCOPE_PROCESS ? (
            <>
              <StatCard label="Fiscal year" hint="filter" value={String(currentYear)} />
              <StatCard label="RCM rows" value={String(processStats.rows)} accent="teal" />
              <StatCard label="SOX cycles" hint="in view" value={String(processStats.cycles)} />
              <StatCard label="Key controls" hint="in view" value={String(processStats.keyControls)} />
            </>
          ) : activeScope === SCOPE_ALL ? (
            <>
              <StatCard label="Fiscal year" hint="filter" value={String(currentYear)} />
              <StatCard label="All RCM rows" value={String(allStats?.rows ?? 0)} accent="teal" />
              <StatCard label="Process + ITGC" hint="combined" value="All" />
              <StatCard label="Key controls" hint="in view" value={String(allStats?.keyControls ?? 0)} />
            </>
          ) : (
            <>
              <StatCard label="Fiscal year" hint="filter" value={String(currentYear)} />
              <StatCard label="ITGC RCM rows" value={String(itgcStats?.rcmRows ?? 0)} accent="teal" />
              <StatCard label="ITGC domains" hint="in scope" value={String(itgcStats?.domains ?? 0)} />
              <StatCard label="Key ITGCs" hint="in view" value={String(itgcStats?.keyControls ?? 0)} />
            </>
          )}
        </div>

        {/* Scope tabs — Process / ITGC */}
        <div className="border-b border-gray-200 mb-2 flex items-center gap-6">
          {[
            { key: SCOPE_ALL, label: 'All', hint: '(combined)', icon: LayoutGrid },
            { key: SCOPE_PROCESS, label: 'Process', hint: '(financial)', icon: Shield },
            { key: SCOPE_ITGC, label: 'ITGC', hint: '(IT general controls)', icon: Cpu },
          ].map((t) => {
            const active = activeScope === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setScope(t.key)}
                className={`relative -mb-px py-2 text-sm font-medium border-b-2 transition-colors ${
                  active ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Icon className="h-4 w-4" aria-hidden /> {t.label}
                  <span className="text-xs text-gray-400 font-normal normal-case">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {activeScope === SCOPE_ITGC ? (
          <RcmItgcView
            reloadToken={itgcReloadToken}
            onStatsChange={setItgcStats}
            onFilteredRowsChange={setItgcFilteredRows}
            onExportColumnsChange={setItgcExportColumnIds}
          />
        ) : activeScope === SCOPE_ALL ? (
          <RcmAllView
            onStatsChange={setAllStats}
            onListParamsChange={handleAllListParamsChange}
            onExportColumnsChange={setAllExportColumnIds}
          />
        ) : !tableLoading && !hasData && !hasActiveListCriteria ? (
          processEmptyState
        ) : (
        <>
        <RcmListFilters
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          filterControlType={filterControlType}
          onFilterControlTypeChange={setFilterControlType}
          filterSignificance={filterSignificance}
          onFilterSignificanceChange={setFilterSignificance}
          filterProcess={filterProcess}
          onProcessFilterChange={handleProcessFilterChange}
          processFilterOptions={processFilterOptions}
          onProcessFilterInputChange={handleProcessFilterInputChange}
          onProcessFilterMenuClose={handleProcessFilterMenuClose}
          onProcessFilterMenuScrollToBottom={handleProcessFilterMenuScrollToBottom}
          loadingMoreProcessFilterOptions={loadingMoreProcessFilterOptions}
          processFilterNoOptionsMessage={processFilterNoOptionsMessage}
          filterControlId={filterControlId}
          onControlFilterChange={handleControlFilterChange}
          controlFilterOptions={controlFilterOptions}
          onControlFilterInputChange={handleControlFilterInputChange}
          onControlFilterMenuClose={handleControlFilterMenuClose}
          controlFilterNoOptionsMessage={controlFilterNoOptionsMessage}
          filterFrequency={filterFrequency}
          onFilterFrequencyChange={setFilterFrequency}
          filterRiskCategory={filterRiskCategory}
          onFilterRiskCategoryChange={setFilterRiskCategory}
          onReset={handleResetFilters}
          columnVisibilityMenu={
            <RcmColumnVisibilityMenu
              columns={getHideableRcmColumns()}
              columnVisibility={rcmColumnVisibility}
              onToggleColumn={handleToggleRcmColumn}
              onReset={handleResetRcmColumns}
              enableReorder
              columnOrder={rcmColumnOrder}
              onReorder={handleReorderRcmColumns}
            />
          }
        />

        <RcmMatrixTable
          showOverlay={showTableOverlay}
          onSortClick={handleSortClick}
          renderSortIcon={renderSortIcon}
          columnVisibility={rcmColumnVisibility}
          columnOrder={rcmColumnOrder}
          showPagination={rcmTotalItems > 0 && !tableLoading}
          paginationFrom={paginationFrom}
          paginationTo={paginationTo}
          totalItems={rcmTotalItems}
          listPage={rcmListPage}
          totalPages={rcmTotalPages}
          pageSize={rcmPageSize}
          onPaginationChange={handleTablePaginationChange}
        >
              {rcmData.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(visibleRcmColumnCount, 1)} className="px-6 py-12 text-center text-sm text-gray-500">
                    No data available.
                  </td>
                </tr>
              ) : (
                rcmData.map((row, index) => (
                  <tr
                    key={row.mappingId || row.id || index}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    {orderedVisibleRcmColumnIds.map((columnId) => {
                      switch (columnId) {
                        case 'srNo':
                          return (
                            <td key={columnId} className="px-3 py-3 text-xs text-gray-600 text-center font-medium border-r border-gray-100">
                              {row.srNo}
                            </td>
                          );
                        case 'classification':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <span
                                className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${
                                  String(row.classification).toLowerCase() === 'itgc'
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'bg-teal-50 text-teal-700'
                                }`}
                              >
                                {row.classification || 'Business'}
                              </span>
                            </td>
                          );
                        case 'soxCycle':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <span className="inline-flex max-w-[180px] items-center rounded bg-blue-50 px-2 py-0.5 text-[11px] leading-tight font-medium text-blue-700">
                                <TruncatedCellValue value={row.soxCycle} field="soxCycle" className="truncate" />
                              </span>
                            </td>
                          );
                        case 'subProcess':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <TruncatedCellValue
                                value={row.subProcess}
                                field="subProcess"
                                className="text-center text-[11px] leading-tight font-normal text-gray-900"
                              />
                            </td>
                          );
                        case 'riskId':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <TruncatedCellValue
                                value={row.riskId || '-'}
                                field="riskId"
                                className="text-center text-[11px] leading-tight font-normal text-gray-600"
                              />
                            </td>
                          );
                        case 'clientRiskId':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <TruncatedCellValue
                                value={row.clientRiskId || '-'}
                                field="clientRiskId"
                                className="text-center text-[11px] leading-tight font-normal text-gray-600"
                              />
                            </td>
                          );
                        case 'riskDescription':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="riskDescription" value={row.riskDescription} />
                            </td>
                          );
                        case 'controlId':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <TruncatedCellValue
                                value={row.controlId || '-'}
                                field="controlId"
                                className="text-center text-[11px] leading-tight font-normal text-gray-600"
                              />
                            </td>
                          );
                        case 'controlObjective':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="controlObjective" value={row.controlObjective} />
                            </td>
                          );
                        case 'controlDescription':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="controlDescription" value={row.controlDescription} />
                            </td>
                          );
                        case 'completeness':
                        case 'existenceOccurrence':
                        case 'accuracy':
                        case 'valuation':
                        case 'rightsObligations':
                          return (
                            <td key={columnId} className="px-2 py-2 text-center border-r border-gray-100 align-middle">
                              <AssertionCell rowIndex={index} field={columnId} value={row[columnId]} />
                            </td>
                          );
                        case 'what':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="what" value={row.what} />
                            </td>
                          );
                        case 'who':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="who" value={row.who} />
                            </td>
                          );
                        case 'keyReports': {
                          const { display, fullLabel, isCompact } = formatRcmKeyReportsPreview(
                            row.keyReports
                          );
                          return (
                            <td
                              key={columnId}
                              className="px-2.5 py-2 border-r border-gray-100 align-middle text-center text-[11px] text-gray-700"
                              title={isCompact ? fullLabel : undefined}
                            >
                              {display}
                            </td>
                          );
                        }
                        case 'when':
                          return (
                            <td key={columnId} className="px-2.5 py-2 border-r border-gray-100 align-middle text-center">
                              <EditableCell rowIndex={index} field="when" value={row.when} />
                            </td>
                          );
                        case 'controlType':
                          return (
                            <td key={columnId} className="px-2.5 py-2 text-center border-r border-gray-100 align-middle">
                              <EditableCell
                                rowIndex={index}
                                field="controlType"
                                value={row.controlType}
                                isDropdown
                                options={['Manual', 'Automated', 'IT-Dependent']}
                              />
                            </td>
                          );
                        case 'keyNonKey':
                          return (
                            <td key={columnId} className="px-2.5 py-2 text-center border-r border-gray-100 align-middle">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded cursor-pointer ${row.keyNonKey === 'Key' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                                }`}
                                onClick={() => {
                                  const newVal = row.keyNonKey === 'Key' ? 'Non-Key' : 'Key';
                                  const updatedData = [...rcmData];
                                  updatedData[index] = { ...updatedData[index], keyNonKey: newVal };
                                  setRcmData(updatedData);
                                  saveOverride(row.mappingId, 'keyNonKey', newVal);
                                }}
                                title="Click to toggle"
                              >
                                {row.keyNonKey}
                              </span>
                            </td>
                          );
                        case 'riskCategory': {
                          const label = row.riskCategory || 'SOX';
                          const badgeClass = RISK_CATEGORY_BADGE_CLASSES[label] || 'bg-gray-100 text-gray-700';
                          return (
                            <td key={columnId} className="px-2.5 py-2 text-center align-middle">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeClass}`}>
                                {label}
                              </span>
                            </td>
                          );
                        }
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))
              )}
        </RcmMatrixTable>
        </>
        )}
      </div>
      <GenericImportRcmModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: rcmQueryKeys.all });
          setItgcReloadToken((n) => n + 1);
          setRcmListPage(1);
        }}
      />
    </div>
  );
}

// One stat widget for the row above the scope tabs. Same shape used by
// both Process and ITGC sets — only the values change per scope.
function StatCard({ label, hint, value, accent }) {
  const valueClass = accent === 'teal' ? 'text-teal-700' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
        {hint ? <span className="ml-1 text-gray-400 font-normal normal-case">({hint})</span> : null}
      </p>
      <p className={`text-lg font-bold mt-0.5 ${valueClass}`}>{value}</p>
    </div>
  );
}

// Small helper rendered inside the Process empty state — keeps the JSX
// in the main return concise.
function Step({ n, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{n}</div>
      <div>
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-500">{body}</p>
      </div>
    </div>
  );
}

export default RiskControlMatrix;
